import { AfterContentChecked, Component, EventEmitter, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatDialog } from '@angular/material';

import { Audit, AuditFilter, AuditStatus } from '../../audit.model';
import { RequestResult, TableData, TableOptions } from '../../../shared/shared.model';
import { AuditService } from '../../audit.service';
import { DatePipe } from '@angular/common';
import { DISPLAY_DATE_FORMAT } from '../../../app.constants';
import { AuditFilterDialogComponent } from '../audit-filter-dialog/audit-filter-dialog.component';

@Component({
  selector: 'iaud-audit-list',
  templateUrl: './audit-list.component.html',
  styleUrls: ['./audit-list.component.scss'],
})
export class AuditListComponent implements OnInit, AfterContentChecked {
  @ViewChild('findingsCellTemplate') findingsCellTemplate: TemplateRef<any>;
  @ViewChild('statusCellTemplate') statusCellTemplate: TemplateRef<any>;
  @ViewChild('SMEsDetailTemplate') SMEsDetailTemplate: TemplateRef<any>;

  auditTableData: TableData<Audit>;
  forceReloadEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
  filterInput: FormControl = new FormControl();
  hasInitialAudits: boolean = true;
  auditFilter: AuditFilter = this.auditService.getAuditFilter();
  auditStatus = AuditStatus;
  auditTableOptions: TableOptions = {
    hrefBase: '/audit',
    actionButtons: true,
    hideDeleteButton : true,
    columns: [
      {
        title: 'Audit ID',
        fieldName: 'auditId',
      },
      {
        title: 'Audit Title',
        fieldName: 'auditTitle',
      },
      {
        title: 'Audit Owner Group',
        fieldName: 'auditOwnerGroup',
      },
      {
        title: 'Findings',
        fieldName: 'numberOfFindings',
      },
      {
        title: 'Audit Status',
        fieldName: 'status',
      },
      {
        title: 'CAPA/EC Activity Status',
        fieldName: 'capaActivityStatus',
      },
      {
        title: 'Audit Due Date',
        fieldName: 'dueDate',
        pipe: new DatePipe('en-US'),
        pipeParams: DISPLAY_DATE_FORMAT,
      },
    ],
    additionalInfo: [
      {
        title: 'Audit Code',
        fieldName: 'auditCode',
      },
      {
        title: 'Audit Type',
        fieldName: 'auditType',
      },
      {
        title: 'External Business Partner',
        fieldName: 'externalPartner',
      },
      {
        title: 'Subject Matter Experts (SMES)',
        fieldName: 'smeApprovers',
      },
      {
        title: 'Audit Description',
        fieldName: 'auditDescription',
      },
    ],
  };

  constructor(
    private route: ActivatedRoute,
    private auditService: AuditService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    const resolverAuditData = this.route.snapshot.data.audit;
    this.auditTableData = resolverAuditData;
    this.hasInitialAudits = resolverAuditData.items.length > 0;

    this.filterInput.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(500)
      ).subscribe(
        () => this.forceReloadEvent.emit(true));
  }

  ngAfterContentChecked(): void {
    this.auditTableOptions.columns[3].customTemplate = this.findingsCellTemplate;
    this.auditTableOptions.columns[5].customTemplate = this.statusCellTemplate;
    this.auditTableOptions.additionalInfo[3].customTemplate = this.SMEsDetailTemplate;
  }

  reloadAuditList(page: number, pageSize: number): Observable<TableData<Audit>> {
    return this.auditService.getAuditList(page, pageSize, this.filterInput.value || '');
  }
  deleteData(id: number): Observable<RequestResult> {
    return this.auditService.deleteAudit(id);
  }

  showFilterDlg(): void {
    const dialogRef = this.dialog.open(AuditFilterDialogComponent, {
      data: this.auditFilter || {},
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === null) {
        this.auditFilter = undefined;
        this.auditTableData.page = 0;
        this.auditService.setAuditFilter(undefined);
        this.forceReloadEvent.emit(true);
      } else if (result) {
        this.auditTableData.page = 0;
        this.auditFilter = result;
        this.auditService.setAuditFilter(this.auditFilter);
        this.forceReloadEvent.emit(true);
      }
    });
  }

  exportXls(): void {
    this.auditService.exportXls(this.auditTableData.page, this.auditTableData.pageSize, this.filterInput.value || '')
      .subscribe(data => {
        const blob = new Blob([data], { type: 'application/vnd.ms-excel' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      });
  }
}
